import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Cloud, HardDrive, LoaderCircle } from 'lucide-react';
import './directory-sync-badge.css';

type SyncStatus = 'NOT_LINKED' | 'PENDING' | 'SYNCED' | 'ERROR' | 'ARCHIVED' | string;

function formatSyncTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
}

export function DirectorySyncBadge({ source, status, lastSyncedAt, compact = false }: {
  source?: string | null;
  status?: SyncStatus | null;
  lastSyncedAt?: string | null;
  compact?: boolean;
}) {
  const fromBitrix = source === 'BITRIX24';
  if (!fromBitrix) return <span className={`directory-sync-badge local ${compact ? 'compact' : ''}`} title="Карточка создана и управляется в Intez Helpdesk"><HardDrive /><span><b>Локальная</b>{!compact && <small>Intez Helpdesk</small>}</span></span>;
  const normalized = String(status || 'PENDING').toUpperCase();
  const state = normalized === 'ERROR'
    ? { className: 'error', label: 'Ошибка синхронизации', icon: <AlertTriangle /> }
    : normalized === 'ARCHIVED'
      ? { className: 'archived', label: 'Архив Битрикс24', icon: <Cloud /> }
      : normalized === 'PENDING'
        ? { className: 'pending', label: 'Ожидает синхронизации', icon: <LoaderCircle /> }
        : { className: 'synced', label: 'Синхронизировано', icon: <CheckCircle2 /> };
  const time = formatSyncTime(lastSyncedAt);
  const title = `${state.label}${time ? ` · ${time}` : ''}. Юридические и контактные поля управляются в Битрикс24`;
  return <span className={`directory-sync-badge ${state.className} ${compact ? 'compact' : ''}`} title={title}>
    <span className="directory-sync-badge__brand">Б24</span>
    <span><b>{compact ? 'Битрикс24' : state.label}</b>{!compact && <small>{time ? `Битрикс24 · ${time}` : 'Битрикс24'}</small>}</span>
    {!compact && state.icon}
  </span>;
}

function initials(value: string) {
  return value.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || '—';
}

export function DirectoryAvatar({ entity, id, name, hasImage, className = '' }: {
  entity: 'company' | 'contact';
  id: string;
  name: string;
  hasImage?: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const path = entity === 'company' ? `/api/v1/companies/${id}/logo` : `/api/v1/contacts/${id}/photo`;
  return <span className={`directory-avatar ${entity} ${className}`} aria-label={name}>
    {hasImage && !failed ? <img src={path} alt="" onError={() => setFailed(true)} /> : <span>{initials(name)}</span>}
  </span>;
}
